import toInteger from 'lodash/toInteger';
import { calculateGradientCoordinate, createCanvas, drawLayerBaseInfo } from '@/utils/file-kit';
import { DynamicType, StretchOptionType, type IBackgroundLayerProps, type ILayerProps } from '@/version-types';

export interface IRenderBackgroundLayerCanvasOptions {
    layer: ILayerProps;
    stageWidth: number;
    stageHeight: number;
    baseImage?: HTMLCanvasElement | HTMLImageElement;
    /** 为 true 时，图片尺寸无效则返回 undefined */
    validateImageSize?: boolean;
    /** 图片模式下，半透明图片渲染时在底部加白底色 */
    drawWhiteBackground?: boolean;
    /** 是否将图层 opacity 烘焙进图片 canvas；edgeless 展示时应为 false，由 render 单独施加 */
    applyLayerOpacity?: boolean;
}

/** 图片模式下的底色：Video/Webp 用 backgroundColor，其余用白底 */
function fillImageModeBackground(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    data: IBackgroundLayerProps,
    drawWhiteBackground?: boolean,
) {
    const { dynamicInfo } = data;
    const isVideoWebpWithBg =
        dynamicInfo?.enableBGColor &&
        (dynamicInfo?.dynamicType === DynamicType.Video || dynamicInfo?.dynamicType === DynamicType.Webp);

    if (isVideoWebpWithBg) {
        ctx.fillStyle = data.backgroundColor;
        ctx.fillRect(0, 0, width, height);
        return;
    }

    if (drawWhiteBackground) {
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, width, height);
    }
}

function shouldDrawImageModeBackground(data: IBackgroundLayerProps, drawWhiteBackground?: boolean) {
    const { dynamicInfo } = data;
    const isVideoWebpWithBg =
        dynamicInfo?.enableBGColor &&
        (dynamicInfo?.dynamicType === DynamicType.Video || dynamicInfo?.dynamicType === DynamicType.Webp);
    return isVideoWebpWithBg || !!drawWhiteBackground;
}

/** 绘制不参与透明度的图片模式底色层（对应 DOM 中白底/Video 背景色的父 div） */
function drawImageModeBackgroundLayer(
    width: number,
    height: number,
    data: IBackgroundLayerProps,
    drawWhiteBackground: boolean | undefined,
    flipH: boolean,
    flipV: boolean,
): HTMLCanvasElement | null {
    if (!shouldDrawImageModeBackground(data, drawWhiteBackground)) {
        return null;
    }
    const { canvas, ctx } = drawLayerBaseInfo(width, height, flipH, flipV, 1);
    fillImageModeBackground(ctx, width, height, data, drawWhiteBackground);
    return canvas;
}

/** 单 canvas：翻转 + 底色（不参与 opacity）+ 图片内容（单独施加 opacity） */
function renderImageModeLayerCanvas(
    width: number,
    height: number,
    data: IBackgroundLayerProps,
    drawWhiteBackground: boolean | undefined,
    flipH: boolean,
    flipV: boolean,
    imageOpacity: number,
    drawImage: (ctx: CanvasRenderingContext2D) => void,
): HTMLCanvasElement {
    const { canvas, ctx } = drawLayerBaseInfo(width, height, flipH, flipV, 1);
    fillImageModeBackground(ctx, width, height, data, drawWhiteBackground);
    if (imageOpacity !== 1) {
        ctx.save();
        ctx.globalAlpha = imageOpacity;
        drawImage(ctx);
        ctx.restore();
    } else {
        drawImage(ctx);
    }
    return canvas;
}

/** 缩放、旋转后裁剪到目标尺寸（合并原 base + transform + result 三步） */
function drawScaledRotatedCroppedImage(
    image: HTMLCanvasElement | HTMLImageElement,
    scaleWidth: number,
    scaleHeight: number,
    offsetX: number,
    offsetY: number,
    imgRotate: number,
    resultWidth: number,
    resultHeight: number,
): HTMLCanvasElement {
    const { canvas: transformCanvas, ctx: transformCtx } = createCanvas(scaleWidth, scaleHeight);
    transformCtx.translate(offsetX + scaleWidth / 2, offsetY + scaleHeight / 2);
    transformCtx.rotate((Math.PI / 180) * imgRotate);
    transformCtx.drawImage(
        image,
        0,
        0,
        image.width,
        image.height,
        -scaleWidth / 2,
        -scaleHeight / 2,
        scaleWidth,
        scaleHeight,
    );
    const { canvas: resultCanvas, ctx: resultCtx } = createCanvas(resultWidth, resultHeight);
    resultCtx.drawImage(
        transformCanvas,
        (scaleWidth - resultWidth) / 2,
        (scaleHeight - resultHeight) / 2,
        resultWidth,
        resultHeight,
        0,
        0,
        resultWidth,
        resultHeight,
    );
    return resultCanvas;
}

export function compositeLayerCanvases(
    bottomCanvas: HTMLCanvasElement | null,
    topCanvas: HTMLCanvasElement,
    width: number,
    height: number,
): HTMLCanvasElement {
    if (!bottomCanvas) {
        return topCanvas;
    }
    const { canvas, ctx } = createCanvas(width, height);
    ctx.drawImage(bottomCanvas, 0, 0);
    ctx.drawImage(topCanvas, 0, 0);
    return canvas;
}

/** 图片模式装饰层：白底 / Video 背景色（不参与 opacity） */
export function buildImageModeDecorCanvas(
    layer: ILayerProps,
    stageWidth: number,
    stageHeight: number,
    drawWhiteBackground?: boolean,
): HTMLCanvasElement | null {
    const data = layer.data as IBackgroundLayerProps;
    if (!data.url) {
        return null;
    }
    const { flipH, flipV } = data.imageTransform;
    return drawImageModeBackgroundLayer(stageWidth, stageHeight, data, drawWhiteBackground, flipH, flipV);
}

/** 在 canvas 底部绘制透明格子背景（对应 app-background-wrap 的 bg.png） */
export function compositeWithGridBackground(
    contentCanvas: HTMLCanvasElement | undefined,
    gridImage: HTMLImageElement | HTMLCanvasElement,
    width: number,
    height: number,
): HTMLCanvasElement | undefined {
    if (!contentCanvas) {
        return undefined;
    }
    const { canvas, ctx } = createCanvas(width, height);
    const pattern = ctx.createPattern(gridImage, 'repeat');
    if (pattern) {
        ctx.fillStyle = pattern;
        ctx.fillRect(0, 0, width, height);
    }
    ctx.drawImage(contentCanvas, 0, 0);
    return canvas;
}

export function renderBackgroundLayerCanvas({
    layer,
    stageWidth,
    stageHeight,
    baseImage,
    validateImageSize = false,
    drawWhiteBackground = false,
    applyLayerOpacity = true,
}: IRenderBackgroundLayerCanvasOptions): HTMLCanvasElement | undefined {
    const { width, height, opacity } = layer;
    const data = layer.data as IBackgroundLayerProps;
    const color = data.backgroundColor;
    const backgroundGradient = data.backgroundGradient || null;
    const { stretchOption, imageTransform, url } = data;
    const { flipH, flipV, scale, rotate: imgRotate } = imageTransform;
    let { heightRatio, widthRatio, offsetX: offsetXRatio, offsetY: offsetYRatio } = imageTransform;
    const imageOpacity = applyLayerOpacity ? opacity : 1;

    // 这些值在写入时存在null的情况，进行容错处理
    heightRatio = heightRatio || 1;
    widthRatio = widthRatio || 1;
    offsetXRatio = offsetXRatio || 0;
    offsetYRatio = offsetYRatio || 0;

    if (url) {
        const image = baseImage ?? createCanvas(1, 1).canvas;
        const naturalHeight = image.height;
        const naturalWidth = image.width;

        if (validateImageSize && (!naturalHeight || !naturalWidth)) {
            return undefined;
        }

        if (stretchOption === StretchOptionType.repeat) {
            const layerWidth = width!;
            const layerHeight = height!;
            return renderImageModeLayerCanvas(
                layerWidth,
                layerHeight,
                data,
                drawWhiteBackground,
                flipH,
                flipV,
                imageOpacity,
                (imageCtx) => {
                    const column = Math.trunc(layerHeight / naturalHeight) + 1;
                    const row = Math.trunc(layerWidth / naturalWidth) + 1;
                    const x = layerWidth / 2 - naturalWidth / 2;
                    const y = layerHeight / 2 - naturalHeight / 2;
                    for (let i = -column; i < column; i++) {
                        for (let j = -row; j < row; j++) {
                            imageCtx.drawImage(
                                image,
                                x + naturalWidth * j,
                                y + naturalHeight * i,
                                naturalWidth,
                                naturalHeight,
                            );
                        }
                    }
                },
            );
            // WARN: 拉伸模式为 fill 的逻辑用途未知，暂时注释
            // } else if (stretchOption === StretchOptionType.fill) {
            //     const res = getDrawContainParams(image.width, image.height, width, height);
            //     layerCtx.drawImage(image, res.dx, res.dy, res.dw, res.dh);
        } else {
            const offsetX = toInteger(offsetXRatio * widthRatio * stageWidth * scale);
            const offsetY = toInteger(offsetYRatio * heightRatio * stageHeight * scale);
            const originImgWidth = toInteger(stageWidth * widthRatio);
            const originImgHeight = toInteger(stageHeight * heightRatio);
            const scaleWidth = toInteger(originImgWidth * scale);
            const scaleHeight = toInteger(originImgHeight * scale);
            const resultWidth = toInteger(stageWidth);
            const resultHeight = toInteger(stageHeight);
            const croppedImage = drawScaledRotatedCroppedImage(
                image,
                scaleWidth,
                scaleHeight,
                offsetX,
                offsetY,
                imgRotate,
                resultWidth,
                resultHeight,
            );
            return renderImageModeLayerCanvas(
                resultWidth,
                resultHeight,
                data,
                drawWhiteBackground,
                flipH,
                flipV,
                imageOpacity,
                (imageCtx) => {
                    imageCtx.drawImage(croppedImage, 0, 0);
                },
            );
        }
    } else if (backgroundGradient) {
        const { canvas: layerCanvas, ctx: layerCtx } = drawLayerBaseInfo(width, height, flipH, flipV, opacity);
        const { colors, angle } = backgroundGradient;
        const { x0, y0, x1, y1 } = calculateGradientCoordinate(stageWidth, stageHeight, angle);
        const gradient = layerCtx.createLinearGradient(x0, y0, x1, y1);
        colors.forEach((item) => {
            const { color, value } = item;
            gradient.addColorStop(value, color);
        });
        layerCtx.fillStyle = gradient;
        layerCtx.fillRect(0, 0, stageWidth, stageHeight);
        return layerCanvas;
    } else if (color) {
        const { canvas: layerCanvas, ctx: layerCtx } = drawLayerBaseInfo(width, height, flipH, flipV, opacity);
        layerCtx.fillStyle = color;
        layerCtx.fillRect(0, 0, stageWidth, stageHeight);
        layer.width = stageWidth;
        layer.height = stageHeight;
        return layerCanvas;
    }

    const { canvas: layerCanvas } = drawLayerBaseInfo(width, height, flipH, flipV, opacity);
    return layerCanvas;
}
